import stripeJs from '@stripe/stripe-js'
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js'

const CARD_DECLINED_MESSAGES: { [name: string]: string } = {
  authentication_required: '不明な理由で拒否されました。他のカードでお試しください。',
  approve_with_id: '不明な理由で拒否されました。他のカードでお試しください。',
  call_issuer: '不明な理由で拒否されました。他のカードでお試しください。',
  card_not_supported: '不明な理由で拒否されました。他のカードでお試しください。',
  card_velocity_exceeded: 'このカードで利用可能な残高を超えています。他のカードでお試しください。',
  currency_not_supported: 'このカードは指定された日本円をサポートしていません。他のカードでお試しください。',
  do_not_honor: '不明な理由で拒否されました。他のカードでお試しください。',
  do_not_try_again: '不明な理由で拒否されました。他のカードでお試しください。',
  duplicate_transaction: '直前に同金額の決済が行われています。重複していないかご確認ください。',
  expired_card: 'カードの有効期限が切れています。他のカードでお試しください。',
  fraudulent: '不明な理由で拒否されました。他のカードでお試しください。',
  generic_decline: '不明な理由で拒否されました。他のカードでお試しください。',
  incorrect_number: 'カード番号が間違っています。再度カード番号のご確認をお願いします。',
  incorrect_cvc: 'CVC番号が正しくありません。再度ご確認をお願いします。',
  incorrect_pin: '入力されたPINは正しくありません。再度ご確認をお願いします。',
  incorrect_zip: '郵便番号が間違っています。再度ご確認をお願いします。',
  insufficient_funds: '購入を完了する為の資金が不足しています。他のカードでお試しください。',
  invalid_account: 'カード、またアカウントが無効です。他のカードでお試しください。',
  invalid_amount: '支払い金額が無効であるか、許可されている金額を超えています。',
  invalid_cvc: 'CVC番号が正しくありません。再度ご確認をお願いします。',
  invalid_expiry_year: 'カードの有効期限が切れています。他のカードでお試しください。',
  invalid_expiry_year_past: 'カードの有効期限が切れています。他のカードでお試しください。',
  invalid_expiry_month_past: 'カードの有効期限が切れています。他のカードでお試しください。',
  invalid_number: 'カード番号が間違っています。再度カード番号のご確認をお願いします。',
  invalid_pin: '入力されたPINは正しくありません。再度ご確認をお願いします。',
  issuer_not_available: 'カード発行会社に連絡できなかったため、支払いを承認できませんでした。',
  lost_card: '不明な理由で拒否されました。他のカードでお試しください。',
  merchant_blacklist: '不明な理由で拒否されました。他のカードでお試しください。',
  new_account_information_available: 'アカウントが無効です。他のカードでお試しください。',
  no_action_taken: '不明な理由で拒否されました。他のカードでお試しください。',
  not_permitted: '不明な理由で拒否されました。他のカードでお試しください。',
  offline_pin_required: 'PINを入力して、もう一度お試しください。',
  online_or_offline_pin_required: 'PINを入力して、もう一度お試しください。',
  pickup_card: 'カードはこの支払いに使用できません。他のカードでお試しください。',
  pin_try_exceeded: 'エラーが発生しました。他のカードでお試しください。',
  processing_error: 'エラーが発生しました。他のカードでお試しください。',
  reenter_transaction: '不明な理由で拒否されました。他のカードでお試しください。',
  restricted_card: '不明な理由で拒否されました。他のカードでお試しください。',
  revocation_of_all_authorizations: '不明な理由で拒否されました。他のカードでお試しください。',
  revocation_of_authorization: '不明な理由で拒否されました。他のカードでお試しください。',
  security_violation: '不明な理由で拒否されました。他のカードでお試しください。',
  service_not_allowed: '不明な理由で拒否されました。他のカードでお試しください。',
  stolen_card: '不明な理由で拒否されました。他のカードでお試しください。',
  stop_payment_order: '不明な理由で拒否されました。他のカードでお試しください。',
  testmode_decline: 'ストライプテストカード番号が使用されました。他のカードでお試しください。',
  try_again_later: '少し時間を空けてから、もう一度お試し頂くか、他のカードでお試しください。',
  withdrawal_count_limit_exceeded: '利用可能な残高またはクレジットの制限を超えています。他のカードでお試しください。',
}

const getErrorMessage = (error: stripeJs.StripeError): string => {
  if (error.code) {
    return (
      CARD_DECLINED_MESSAGES[error.code] ||
      `不明な理由で拒否されました。他のカードでお試しください。 code: ${error.code}`
    )
    // return (
    //   ERROR_MESSAGES[error.code] ||
    //   `不明なエラーが発生しました。しばらく経ってからお試しください。 code: ${error.code}`
    // );
  } else {
    return '不明なエラーです。しばらく経ってからお試しください。'
  }
}

export const getPaymentMethod = async (
  elements: stripeJs.StripeElements | null,
  stripe: stripeJs.Stripe | null
): Promise<{
  payment_method_id: string | undefined
  message: string
}> => {
  //*********************** START VALIDATION **************************//
  // Stripe.js has not loaded yet. Make sure to disable
  // form submission until Stripe.js has loaded.
  if (!stripe || !elements) throw new Error('Does not exist stripe or elements')

  // Use your card Element with other Stripe.js APIs
  // Get a reference to a mounted CardInputForm.
  const cardNumberElement = elements.getElement(CardNumberElement)
  const cardExpiryElement = elements.getElement(CardExpiryElement)
  const cardCvcElement = elements.getElement(CardCvcElement)
  if (!cardNumberElement) throw new Error('Does not exist cardNumberElement')
  if (!cardExpiryElement) throw new Error('Does not exist cardExpiryElement')
  if (!cardCvcElement) throw new Error('Does not exist cardCvcElement')

  const { error, paymentMethod } = await stripe.createPaymentMethod({
    type: 'card',
    card: cardNumberElement,
  })

  if (error)
    return {
      payment_method_id: undefined,
      message: getErrorMessage(error),
    }
  if (!paymentMethod)
    return {
      payment_method_id: undefined,
      message: '無効なクレジットカード、または入力した情報に誤りがあります。',
    }

  return {
    payment_method_id: paymentMethod.id,
    message: '',
  }
}
